import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { shipmentTravelProgress, useRouteGeocode } from '#/entities/shipment'
import type { GeocodedPoint } from '#/entities/shipment'
import type {
  ShipmentDetail,
  StopResponse,
} from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import { MapboxMap } from '#/shared/ui/mapbox-map'
import type { MapboxMapLocation } from '#/shared/ui/mapbox-map'
import { OriginIcon } from '#/shared/ui/icons'

const STOP_TYPE_PICKUP = 'pickup'
const STOP_TYPE_DELIVERY = 'delivery'

interface RouteEndpoint {
  label: string
  query: string
  coords?: [number, number]
}

function stopToEndpoint(
  stop: StopResponse | undefined,
): RouteEndpoint | undefined {
  if (!stop) return undefined
  const text = [stop.city, stop.state, stop.zip].filter(Boolean).join(', ')
  const coords: [number, number] | undefined =
    stop.lat != null && stop.lon != null ? [stop.lat, stop.lon] : undefined
  if (!coords && !text) return undefined
  return { label: text, query: text, coords }
}

function pickEndpoints(stops: StopResponse[] | undefined): {
  origin: RouteEndpoint | undefined
  destination: RouteEndpoint | undefined
} {
  if (!stops || stops.length === 0)
    return { origin: undefined, destination: undefined }
  const pickups = stops.filter((s) => s.type === STOP_TYPE_PICKUP)
  const deliveries = stops.filter((s) => s.type === STOP_TYPE_DELIVERY)
  return {
    origin: stopToEndpoint(pickups[0] ?? stops[0]),
    destination: stopToEndpoint(
      deliveries[deliveries.length - 1] ?? stops[stops.length - 1],
    ),
  }
}

function toMapLocation(
  endpoint: RouteEndpoint | undefined,
  geocoded: GeocodedPoint | undefined,
): MapboxMapLocation | undefined {
  if (!endpoint) return undefined
  const coords =
    endpoint.coords ?? (geocoded ? [geocoded.lat, geocoded.lng] : undefined)
  if (!coords) return undefined
  return { label: endpoint.label, coords }
}

function geocodeQuery(endpoint: RouteEndpoint | undefined): string | undefined {
  if (!endpoint || endpoint.coords) return undefined
  return endpoint.query
}

function MapNotice({
  tone,
  message,
  className,
}: {
  tone: 'muted' | 'error'
  message: string
  className: string
}) {
  const isError = tone === 'error'
  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-md border border-dashed',
        className,
        isError ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50',
      )}
    >
      <div className="flex max-w-xs flex-col items-center gap-2 px-5 text-center">
        <OriginIcon
          className={cn('size-6', isError ? 'text-red-400' : 'text-gray-300')}
        />
        <span
          className={cn('text-sm', isError ? 'text-red-500' : 'text-gray-400')}
        >
          {message}
        </span>
      </div>
    </div>
  )
}

interface ShipmentRouteMapProps {
  stops: StopResponse[] | undefined
  status: ShipmentDetail['status'] | undefined
  isLoading?: boolean
  className: string
}

export function ShipmentRouteMap({
  stops,
  status,
  isLoading = false,
  className,
}: ShipmentRouteMapProps) {
  const { t } = useTranslation()
  const { origin, destination } = useMemo(() => pickEndpoints(stops), [stops])
  const progress = shipmentTravelProgress(status)
  const geocode = useRouteGeocode(
    geocodeQuery(origin),
    geocodeQuery(destination),
    true,
  )
  const mapOrigin = useMemo(
    () => toMapLocation(origin, geocode.origin),
    [origin, geocode.origin],
  )
  const mapDestination = useMemo(
    () => toMapLocation(destination, geocode.destination),
    [destination, geocode.destination],
  )

  if (isLoading || geocode.isLoading)
    return (
      <div className={cn('animate-pulse rounded-md bg-gray-100', className)} />
    )
  if (!origin || !destination)
    return (
      <MapNotice
        tone="muted"
        message={t('quoteDetails.map.empty')}
        className={className}
      />
    )
  if (geocode.isError || !mapOrigin || !mapDestination)
    return (
      <MapNotice
        tone="error"
        message={t('quoteDetails.map.routeError')}
        className={className}
      />
    )
  return (
    <MapboxMap
      origin={mapOrigin}
      destination={mapDestination}
      progress={progress}
      className={className}
    />
  )
}
